import { Component, effect, inject } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { TableModule } from 'primeng/table';
import { HeaderComponent } from './components/header/header.component';
import { NgIf } from '@angular/common';
import Keycloak, { KeycloakProfile } from 'keycloak-js';
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEventType, ReadyArgs, typeEventArgs } from 'keycloak-angular';
import { environment } from '../environments/environment';
import {
    DevToolService,
    MessagingService,
    PasswordResetNotificationService,
    SecurityDomain,
    SessionStorageService,
    ThemeSwitchService
} from 'lib';
import { FooterComponent } from './components/footer/footer.component';
import { DomainDetectorService } from './services/domain-detector.service';

@Component({
    selector: 'mmoga-root',
    standalone: true,
    imports: [RouterOutlet, TableModule, HeaderComponent, FooterComponent, NgIf],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})
export class AppComponent {
    private readonly keycloak = inject(Keycloak);
    private readonly router = inject(Router);
    private readonly devToolService = inject(DevToolService);
    private readonly sessionStorageService = inject(SessionStorageService);
    private readonly domainDetectorService = inject(DomainDetectorService);
    private readonly themeSwitchService = inject(ThemeSwitchService);
    private readonly messagingService = inject(MessagingService);
    private readonly passwordResetNotificationService = inject(PasswordResetNotificationService);

    private DEVTOOL_ROLE = 'DevTool';
    private hasAttemptedMessagingConnection = false;

    public isLoggedIn = false;
    userProfile?: KeycloakProfile;
    public appVersion: string = environment.appVersion;
    public isEyefinity = false;

    constructor() {
        const keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);

        effect(() => {
            const keycloakEvent = keycloakSignal();

            if (keycloakEvent.type === KeycloakEventType.Ready) {
                this.handleReadyEvent(keycloakEvent.args);
            }

            if (keycloakEvent.type === KeycloakEventType.AuthSuccess) {
                this.handleAuthSuccess();
            }

            if (keycloakEvent.type === KeycloakEventType.AuthLogout) {
                this.handleAuthLogout();
            }
        });
    }

    private handleReadyEvent(args: unknown): void {
        this.isLoggedIn = typeEventArgs<ReadyArgs>(args);
        void this.checkEnterpriseData();
        if (this.isLoggedIn) {
            this.connectMessaging();
            this.loadUserProfileAndNavigate();
        }
    }

    private handleAuthSuccess(): void {
        this.isLoggedIn = true;
        this.connectMessaging();
        this.loadUserProfileAndNavigate();
    }

    private handleAuthLogout(): void {
        this.isLoggedIn = false;
        this.userProfile = undefined;
        this.hasAttemptedMessagingConnection = false;
        this.passwordResetNotificationService.unsubscribe();
        this.messagingService.disconnect();
        this.sessionStorageService.clear();
    }

    private loadUserProfileAndNavigate(): void {
        this.keycloak.loadUserProfile()
            .then(profile => {
                this.userProfile = profile;
                if (this.keycloak.hasRealmRole(this.DEVTOOL_ROLE)) {
                    this.setDevtoolAccess();
                }
            })
            .finally(() => {
                void this.router.navigate(['home']);
            });
    }

    public startSession() {
        void this.keycloak.login();
    }

    public endSession() {
        this.sessionStorageService.clear();
        void this.keycloak.logout();
    }

    setDevtoolAccess(): void {
        this.devToolService.enableAccess();
    }

    private async checkEnterpriseData(): Promise<void> {
        const domainConfig = await this.domainDetectorService.detectDomain();
        this.isEyefinity = domainConfig.securityDomain === SecurityDomain.EYEFINITY;
        this.themeSwitchService.setEyefinityTheme(this.isEyefinity);
    }

    private connectMessaging(): void {
        if (this.hasAttemptedMessagingConnection) {
            return;
        }

        this.hasAttemptedMessagingConnection = true;

        if (!this.messagingService.connect()) {
            console.error('Failed to connect to messaging service');
            return;
        }

        this.passwordResetNotificationService.subscribeToGlobalPasswordResetNotifications();
    }
}
